import React, { useEffect, useRef, useState } from "react";
import Logo from "./assets/img/web/logo.webp";
import HeroImage from "./assets/img/web/hero-machinery.webp";
import TeamImage from "./assets/img/web/team-installation.webp";
import MapImage from "./assets/img/web/location-map.webp";
import { clients } from "./clientData";
import { company, navigation, serviceFilters, services } from "./siteData";

function Icon({ name, size = 24, className = "" }) {
  const paths = {
    arrow: (
      <>
        <path d="M4 12h15M13 5l7 7-7 7" />
      </>
    ),
    diagonal: (
      <>
        <path d="M6 18 18 6M6 6h12v12" />
      </>
    ),
    down: (
      <>
        <path d="M12 4v16M5 13l7 7 7-7" />
      </>
    ),
    phone: (
      <path d="M8 3H5a2 2 0 0 0-2 2c0 8.8 7.2 16 16 16a2 2 0 0 0 2-2v-3l-5-2-2 2a14 14 0 0 1-6-6l2-2-2-5Z" />
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="1" />
        <path d="m3 6 9 7 9-7" />
      </>
    ),
    pin: (
      <>
        <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 0 1 14 0Z" />
        <circle cx="12" cy="10" r="2" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    menu: (
      <>
        <path d="M4 6h16M4 12h16M4 18h16" />
      </>
    ),
    close: (
      <>
        <path d="m6 6 12 12M18 6 6 18" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    wrench: (
      <>
        <path d="m14 6 4 4 3-3a6 6 0 0 1-8 8l-6 6-4-4 6-6a6 6 0 0 1 8-8l-3 3Z" />
        <path d="m5 17 2 2" />
      </>
    ),
    machine: (
      <>
        <rect x="5" y="8" width="14" height="12" rx="1" />
        <path d="M3 20h18M8 8V3h8v5M8 3l8 5M9 12h6M10 20v-4h4v4M3 12h2M19 12h2" />
      </>
    ),
    move: (
      <>
        <path d="M3 15h12V6H3v9Zm12-5h3l3 4v3h-2M8 17h7M3 15v2h1" />
        <circle cx="6" cy="17" r="2" />
        <circle cx="17" cy="17" r="2" />
        <path d="M5 3h9M11 1l3 2-3 2" />
      </>
    ),
    pipe: (
      <>
        <path d="M3 15h5V8h10v7h3M3 20h10v-7h0v7h8M6 13v9M19 13v9M6 8h4M16 8h4M13 3v5M10 3h6" />
      </>
    ),
    hydraulic: (
      <>
        <path d="M9 3h6M12 3v5M7 8h10v9H7zM12 17v4M9 21h6M4 10h3M17 15h3" />
        <path d="m13 10-3 3h4l-3 3" />
      </>
    ),
    structure: (
      <>
        <path d="M3 21h18M5 21V3h14v18M5 3l14 9H5l14 9M5 12l14-9M5 21l14-9" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    handshake: (
      <>
        <path d="m3 8 5-4 4 2 4-2 5 4-3 9-6 4-6-4-3-9Z" />
        <path d="m7 11 5-5 5 4-3 3-4-2M6 17l3-3 6 5" />
      </>
    ),
    facebook: (
      <path d="M14 21v-8h3l1-4h-4V7c0-1 .5-2 2-2h2V2h-3c-3 0-5 2-5 5v2H7v4h3v8" />
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {paths[name] || paths.arrow}
    </svg>
  );
}

export default function Homepage() {
  const [activeSection, setActiveSection] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedService, setSelectedService] = useState(null);
  const [allClients, setAllClients] = useState(false);
  const [inquiryService, setInquiryService] = useState("");
  const [draftLink, setDraftLink] = useState("");
  const menuButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const serviceSelectRef = useRef(null);
  const headerRef = useRef(null);

  // Track real section positions so navigation works at every viewport size.
  useEffect(() => {
    let frame = 0;
    const updateSection = () => {
      const headerHeight = headerRef.current
        ? headerRef.current.offsetHeight
        : 88;
      let current = "home";
      navigation.forEach(({ id }) => {
        const section = document.getElementById(id);
        if (
          section &&
          section.getBoundingClientRect().top <= headerHeight + 110
        )
          current = id;
      });
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4
      )
        current = "contact";
      setActiveSection(current);
      frame = 0;
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateSection);
    };
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("load", scheduleUpdate);
    updateSection();
    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("load", scheduleUpdate);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth > 900) setMenuOpen(false);
    };
    const onFocusIn = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target))
        setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("resize", onResize);
    };
  }, [menuOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!selectedService) {
      if (dialog.open) dialog.close();
      return undefined;
    }
    dialog.showModal();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [selectedService]);

  const visibleServices = services.filter(
    (service) => filter === "all" || service.category === filter
  );
  const visibleClients = allClients ? clients : clients.slice(0, 12);

  function navigateTo(id) {
    setActiveSection(id);
    setMenuOpen(false);
  }

  function askAboutService(service) {
    setInquiryService(service.title);
    setDraftLink("");
    dialogRef.current.close();
    setSelectedService(null);
    window.requestAnimationFrame(() => {
      const form = serviceSelectRef.current.form;
      form.elements.namedItem("name").focus({ preventScroll: true });
      form.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
      setActiveSection("contact");
      window.history.replaceState(null, "", "#contact");
    });
  }

  function prepareInquiry(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const phoneInput = event.currentTarget.elements.namedItem("phone");
    const phoneDigits = data.get("phone").replace(/\D/g, "");
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      phoneInput.setCustomValidity(
        "กรุณากรอกเบอร์โทรศัพท์ที่มีตัวเลข 7–15 หลัก"
      );
      phoneInput.reportValidity();
      return;
    }
    const detailsInput = event.currentTarget.elements.namedItem("details");
    if (!data.get("details").trim()) {
      detailsInput.setCustomValidity("กรุณาระบุรายละเอียดงานที่ต้องการสอบถาม");
      detailsInput.reportValidity();
      return;
    }
    const subject =
      "สอบถามบริการ MPC: " + (data.get("service") || "ปรึกษางานเครื่องจักร");
    const message = [
      "เรียน ทีมงาน Mechanic Professional",
      "",
      "สนใจสอบถามบริการของบริษัท โดยมีรายละเอียดดังนี้",
      "",
      "ชื่อผู้ติดต่อ: " + data.get("name").trim(),
      "บริษัท: " + (data.get("company").trim() || "-"),
      "เบอร์ติดต่อ: " + data.get("phone").trim(),
      "บริการที่สนใจ: " + (data.get("service") || "ขอคำแนะนำจากทีมงาน"),
      "",
      "รายละเอียดงาน:",
      data.get("details").trim(),
      "",
      "ขอบคุณครับ/ค่ะ",
    ].join("\r\n");
    // The visitor reviews and sends in their email app; no backend is required.
    const link =
      "mailto:" +
      company.email +
      "?subject=" +
      encodeURIComponent(subject) +
      "&body=" +
      encodeURIComponent(message);
    setDraftLink(link);
    window.location.href = link;
  }

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        ข้ามไปยังเนื้อหาหลัก
      </a>
      <div className="utility-bar">
        <div className="container utility-inner">
          <span>
            INDUSTRIAL MACHINERY SPECIALIST{" "}
            <span className="utility-divider">/</span> SINCE 2007
          </span>
          <a href={"mailto:" + company.email}>
            <Icon name="mail" size={14} />
            {company.email}
          </a>
        </div>
      </div>

      <header className="site-header" ref={headerRef}>
        <div className="container header-inner">
          <a
            className="brand"
            href="#home"
            onClick={() => navigateTo("home")}
            aria-label="MPC Mechanic Professional — หน้าแรก"
          >
            <img src={Logo} width="110" height="60" alt="MPC" />
            <span className="brand-name">
              MECHANIC
              <br />
              <span>PROFESSIONAL CO., LTD.</span>
            </span>
          </a>
          <button
            ref={menuButtonRef}
            className="menu-toggle"
            type="button"
            aria-controls="main-navigation"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "ปิดเมนู" : "เปิดเมนู"}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Icon name={menuOpen ? "close" : "menu"} />
          </button>
          <nav
            id="main-navigation"
            className={"main-navigation" + (menuOpen ? " is-open" : "")}
            aria-label="เมนูหลัก"
          >
            {navigation.map((item) => (
              <a
                key={item.id}
                href={"#" + item.id}
                aria-current={
                  activeSection === item.id ? "location" : undefined
                }
                onClick={() => navigateTo(item.id)}
              >
                {item.label}
              </a>
            ))}
            <a className="mobile-nav-phone" href={company.phoneHref}>
              <Icon name="phone" size={18} />
              {company.phone}
            </a>
          </nav>
          <a
            className="button button-small header-contact"
            href={company.phoneHref}
          >
            <Icon name="phone" size={17} />
            <span>ปรึกษางาน</span>
            <Icon name="diagonal" size={16} />
          </a>
        </div>
      </header>

      {menuOpen && (
        <button
          type="button"
          className="menu-backdrop"
          aria-label="ปิดเมนูด้านหลัง"
          tabIndex="-1"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <main id="main-content">
        <section className="hero" id="home" aria-labelledby="hero-title">
          <img
            className="hero-image"
            src={HeroImage}
            width="1706"
            height="960"
            alt="เครื่องจักรอุตสาหกรรมและรายละเอียดชิ้นส่วนโลหะ"
            fetchpriority="high"
          />
          <div className="hero-shade" />
          <div className="hero-grid" aria-hidden="true" />
          <div className="container hero-inner">
            <div className="hero-copy">
              <p className="eyebrow light">
                <span className="red-line" />
                PRECISION. COMMITMENT. PROFESSIONAL.
              </p>
              <h1 id="hero-title">
                ดูแลทุกเครื่องจักร
                <br />
                ให้ธุรกิจ<span>เดินหน้า</span>
              </h1>
              <p className="hero-description">
                ติดตั้ง ขนย้าย และซ่อมบำรุงเครื่องจักรอุตสาหกรรม
                <br className="desktop-break" />{" "}
                ด้วยความเชี่ยวชาญและความใส่ใจในทุกรายละเอียด
              </p>
              <div className="hero-actions">
                <a
                  href="#contact"
                  className="button"
                  onClick={() => navigateTo("contact")}
                >
                  ปรึกษางานกับเรา
                  <Icon name="diagonal" size={20} />
                </a>
                <a
                  href="#service"
                  className="button button-outline"
                  onClick={() => navigateTo("service")}
                >
                  สำรวจบริการ
                  <Icon name="down" size={18} />
                </a>
              </div>
              <p className="hero-company">
                บริษัท เมคคานิก โปรเฟสชั่นแนล จำกัด
              </p>
            </div>
            <div className="hero-coordinate" aria-hidden="true">
              <span>EXPERTISE IN MOTION</span>
              <span className="coordinate-mark">+</span>
              <span>MPC — 01</span>
            </div>
            <div className="hero-caption">
              <span className="hero-caption-line" />
              <span>
                ENGINEERING CARE.
                <br />
                <strong>IN EVERY DETAIL.</strong>
              </span>
            </div>
            <a
              className="hero-scroll"
              href="#about"
              onClick={() => navigateTo("about")}
            >
              <span>รู้จักเราให้มากขึ้น</span>
              <Icon name="down" size={17} />
            </a>
          </div>
        </section>

        <div className="expertise-bar">
          <div className="container expertise-inner">
            <div className="expertise-item since-item">
              <span className="since-year">
                2007<span>↗</span>
              </span>
              <span>
                ก่อตั้งตั้งแต่ พ.ศ. 2550
                <br />
                <small>EXPERIENCE YOU CAN RELY ON</small>
              </span>
            </div>
            <div className="expertise-item">
              <Icon name="machine" size={31} />
              <span>
                ดูแลเครื่องจักรครบด้าน
                <small>INSTALL · RELOCATE · MAINTAIN</small>
              </span>
            </div>
            <div className="expertise-item">
              <Icon name="target" size={31} />
              <span>
                ใส่ใจในทุกรายละเอียด<small>PRECISION IN EVERY PROCESS</small>
              </span>
            </div>
          </div>
        </div>

        <section
          className="about-section section-space"
          id="about"
          aria-labelledby="about-title"
        >
          <div className="container about-layout">
            <div className="about-visual">
              <div className="image-corner" aria-hidden="true" />
              <img
                src={TeamImage}
                width="1600"
                height="1200"
                loading="lazy"
                decoding="async"
                alt="ทีมช่างกำลังปฏิบัติงานกับเครื่องจักรอุตสาหกรรม"
              />
              <div className="about-image-caption">
                <span className="small-label">
                  THE PEOPLE BEHIND THE PRECISION
                </span>
                <span>ประสบการณ์จริง ใส่ใจทุกหน้างาน</span>
              </div>
              <div className="experience-stamp">
                <span>EST.</span>
                <strong>2007</strong>
                <span>MECHANIC PROFESSIONAL</span>
              </div>
            </div>
            <div className="about-copy">
              <p className="eyebrow">
                <span className="red-line" />
                01 — ABOUT MPC
              </p>
              <h2 id="about-title">
                มากกว่างานเครื่องจักร
                <br />
                คือความไว้วางใจ<span className="accent-dot">.</span>
              </h2>
              <p className="section-intro">
                เมคคานิก โปรเฟสชั่นแนล
                <br />
                <span>งานช่างอุตสาหกรรมที่ให้ความสำคัญกับรายละเอียด</span>
              </p>
              <p className="body-copy">
                ตั้งแต่ปี พ.ศ. 2550 เราให้บริการติดตั้ง ถอด ประกอบ ขนย้าย
                และซ่อมบำรุงเครื่องจักรอุตสาหกรรม พร้อมงานระบบท่อ ปั๊ม วาล์ว
                และงานโครงสร้าง
                เพื่อรองรับความต้องการที่แตกต่างกันของแต่ละโรงงาน
              </p>
              <p className="body-copy">
                เรายึดความซื่อสัตย์ ความใส่ใจในงาน
                และกำหนดส่งมอบที่ตกลงร่วมกันเป็นหัวใจของการทำงาน
                เพื่อสร้างความสัมพันธ์ที่ดีในระยะยาวกับลูกค้า
              </p>
              <div className="about-values">
                <span>
                  <Icon name="check" size={18} />
                  ใส่ใจรายละเอียด
                </span>
                <span>
                  <Icon name="check" size={18} />
                  จริงใจต่อลูกค้า
                </span>
                <span>
                  <Icon name="check" size={18} />
                  ประสานงานอย่างใกล้ชิด
                </span>
                <span>
                  <Icon name="check" size={18} />
                  มุ่งมั่นตามแผนส่งมอบ
                </span>
              </div>
              <a
                className="text-link"
                href="#service"
                onClick={() => navigateTo("service")}
              >
                ทำความรู้จักบริการของเรา
                <Icon name="arrow" size={20} />
              </a>
            </div>
          </div>
        </section>

        <section
          className="services-section section-space"
          id="service"
          aria-labelledby="service-title"
        >
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow">
                  <span className="red-line" />
                  02 — OUR EXPERTISE
                </p>
                <h2 id="service-title">
                  ทุกงานอุตสาหกรรม
                  <br />
                  มีเราเป็นส่วนหนึ่ง<span className="accent-dot">.</span>
                </h2>
              </div>
              <p className="heading-description">
                จากเครื่องจักรหนึ่งหน่วย ถึงทั้งไลน์การผลิต
                <br />
                เลือกบริการที่เหมาะกับงานของคุณ
              </p>
            </div>
            <div
              className="service-filters"
              role="group"
              aria-label="กรองประเภทบริการ"
            >
              {serviceFilters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={filter === item.id ? "is-active" : ""}
                  aria-pressed={filter === item.id}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                  <span>
                    {item.id === "all"
                      ? services.length
                      : services.filter(
                          (service) => service.category === item.id
                        ).length}
                  </span>
                </button>
              ))}
            </div>
            <p className="sr-only" role="status">
              แสดง {visibleServices.length} บริการ
            </p>
            <div className="services-grid">
              {visibleServices.map((service) => (
                <article className="service-card" key={service.id}>
                  <div className="service-card-top">
                    <span className="service-icon">
                      <Icon name={service.icon} size={36} />
                    </span>
                    <span className="service-number">{service.number}</span>
                  </div>
                  <p className="small-label service-english">
                    {service.english}
                  </p>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <button
                    type="button"
                    className="service-more"
                    onClick={() => setSelectedService(service)}
                    aria-label={"รายละเอียดบริการ " + service.title}
                  >
                    <span>รายละเอียดบริการ</span>
                    <span className="arrow-box">
                      <Icon name="diagonal" size={19} />
                    </span>
                  </button>
                </article>
              ))}
            </div>
            <div className="services-note">
              <span>มีงานเฉพาะทาง หรือต้องการคำแนะนำเพิ่มเติม?</span>
              <a
                href="#contact"
                className="text-link"
                onClick={() => navigateTo("contact")}
              >
                พูดคุยกับทีมงาน MPC
                <Icon name="arrow" size={19} />
              </a>
            </div>
          </div>
        </section>

        <section
          className="commitment-section"
          aria-labelledby="commitment-title"
        >
          <div className="container commitment-layout">
            <div className="commitment-heading">
              <p className="eyebrow light">
                <span className="red-line" />
                THE MPC COMMITMENT
              </p>
              <h2 id="commitment-title">
                ใส่ใจทุกขั้นตอน
                <br />
                ของงานที่คุณมอบให้
              </h2>
            </div>
            <div className="commitment-item">
              <Icon name="target" size={30} />
              <h3>รายละเอียดสำคัญเสมอ</h3>
              <p>ให้ความสำคัญกับลักษณะงานและความต้องการของแต่ละโรงงาน</p>
            </div>
            <div className="commitment-item">
              <Icon name="handshake" size={30} />
              <h3>ทำงานด้วยความจริงใจ</h3>
              <p>สื่อสารและประสานงานกับลูกค้า เพื่อให้เข้าใจขอบเขตงานร่วมกัน</p>
            </div>
            <div className="commitment-item">
              <Icon name="clock" size={30} />
              <h3>ใส่ใจแผนการส่งมอบ</h3>
              <p>มุ่งมั่นดำเนินงานตามกำหนดเวลาที่วางแผนและตกลงร่วมกัน</p>
            </div>
          </div>
        </section>

        <section
          className="clients-section section-space"
          id="customer"
          aria-labelledby="customer-title"
        >
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow">
                  <span className="red-line" />
                  03 — OUR CUSTOMERS
                </p>
                <h2 id="customer-title">
                  ความไว้วางใจที่ขับเคลื่อนเรา
                  <span className="accent-dot">.</span>
                </h2>
              </div>
              <p className="heading-description">
                ส่วนหนึ่งของลูกค้าที่ให้เรา
                <br />
                ได้เป็นส่วนหนึ่งในงานอุตสาหกรรม
              </p>
            </div>
            <div className="clients-grid" id="customer-logos">
              {visibleClients.map((client) => (
                <div className="client-logo" key={client.id}>
                  <img
                    src={client.image}
                    alt={client.name}
                    title={client.name}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
            <div className="clients-action">
              <button
                className="button button-bordered"
                type="button"
                aria-expanded={allClients}
                aria-controls="customer-logos"
                onClick={() => setAllClients(!allClients)}
              >
                {allClients ? "แสดงน้อยลง" : "ดูลูกค้าทั้งหมด"}
                <Icon name={allClients ? "close" : "plus"} size={18} />
              </button>
            </div>
          </div>
        </section>

        <section
          className="contact-section section-space"
          id="contact"
          aria-labelledby="contact-title"
        >
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow">
                  <span className="red-line" />
                  04 — LET’S WORK TOGETHER
                </p>
                <h2 id="contact-title">
                  เริ่มต้นงานถัดไป
                  <br />
                  ไปกับเรา<span className="accent-dot">.</span>
                </h2>
              </div>
              <p className="heading-description">
                บอกเราเกี่ยวกับงานของคุณ
                <br />
                เพื่อประเมินรายละเอียดและขอบเขตงานร่วมกัน
              </p>
            </div>
            <div className="contact-layout">
              <div className="contact-details">
                <a className="contact-phone" href={company.phoneHref}>
                  <span className="contact-icon">
                    <Icon name="phone" size={26} />
                  </span>
                  <span>
                    <small>พูดคุยกับทีมงาน</small>
                    <strong>{company.phone}</strong>
                  </span>
                  <Icon name="diagonal" size={26} />
                </a>
                <div className="contact-detail">
                  <Icon name="mail" size={23} />
                  <div>
                    <h3>อีเมล</h3>
                    <a href={"mailto:" + company.email}>{company.email}</a>
                  </div>
                </div>
                <div className="contact-detail">
                  <Icon name="pin" size={23} />
                  <div>
                    <h3>บริษัท เมคคานิก โปรเฟสชั่นแนล จำกัด</h3>
                    <address>{company.address}</address>
                    <p className="contact-address-en">
                      {company.englishAddress}
                    </p>
                  </div>
                </div>
                <div className="contact-secondary">
                  <span>แฟกซ์ {company.fax}</span>
                  <a
                    href={company.facebook}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label="Facebook ของ MPC (เปิดแท็บใหม่)"
                  >
                    <Icon name="facebook" size={17} />
                    ติดตาม MPC
                    <Icon name="diagonal" size={15} />
                  </a>
                </div>
                <a
                  className="map-card"
                  href={company.maps}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="เปิดที่ตั้งบริษัทใน Google Maps (เปิดแท็บใหม่)"
                >
                  <img
                    src={MapImage}
                    width="1400"
                    height="989"
                    alt="แผนที่การเดินทางไปบริษัท เมคคานิก โปรเฟสชั่นแนล ที่ลำลูกกา ปทุมธานี"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>
                    <span>
                      <Icon name="pin" size={18} />
                      ลำลูกกา · ปทุมธานี
                    </span>
                    <span>
                      เปิดใน Google Maps
                      <Icon name="diagonal" size={17} />
                    </span>
                  </span>
                </a>
              </div>
              <form
                className="inquiry-form"
                onSubmit={prepareInquiry}
                onChange={(event) => {
                  setDraftLink("");
                  if (event.target.setCustomValidity)
                    event.target.setCustomValidity("");
                }}
              >
                <p className="small-label">TELL US ABOUT YOUR PROJECT</p>
                <h3>ปรึกษางาน / สอบถามบริการ</h3>
                <p className="form-intro">
                  กรอกรายละเอียดเบื้องต้น แล้วส่งต่อให้ทีมงานทางอีเมล
                </p>
                <div className="form-row">
                  <label>
                    ชื่อผู้ติดต่อ <span className="required">*</span>
                    <input
                      name="name"
                      autoComplete="name"
                      required
                      maxLength="100"
                      pattern=".*\S.*"
                      placeholder="ชื่อ–นามสกุล"
                    />
                  </label>
                  <label>
                    บริษัท
                    <input
                      name="company"
                      autoComplete="organization"
                      maxLength="150"
                      placeholder="ชื่อบริษัท (ถ้ามี)"
                    />
                  </label>
                </div>
                <label>
                  เบอร์โทรศัพท์ <span className="required">*</span>
                  <input
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    maxLength="30"
                    pattern="[+0-9.\(\)\s\-]{8,30}"
                    title="กรอกเบอร์ติดต่อ 8–30 ตัวอักษร ใช้ตัวเลข เครื่องหมาย + วงเล็บ ช่องว่าง หรือขีด"
                    placeholder="เบอร์โทรศัพท์ที่ติดต่อได้"
                  />
                </label>
                <label>
                  บริการที่สนใจ
                  <select
                    name="service"
                    ref={serviceSelectRef}
                    value={inquiryService}
                    onChange={(event) => setInquiryService(event.target.value)}
                  >
                    <option value="">เลือกบริการ / ขอคำแนะนำ</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  รายละเอียดงาน <span className="required">*</span>
                  <textarea
                    name="details"
                    required
                    maxLength="1200"
                    rows="4"
                    placeholder="ประเภทเครื่องจักร ลักษณะงาน สถานที่ และช่วงเวลาที่ต้องการ"
                  />
                </label>
                <p className="form-note">
                  <Icon name="mail" size={16} />
                  <span>
                    ปุ่มด้านล่างจะเปิดแอปอีเมลของคุณ
                    เพื่อตรวจสอบรายละเอียดและกดส่งถึงทีมงานอีกครั้ง
                  </span>
                </p>
                <button className="button form-submit" type="submit">
                  เปิดอีเมลเพื่อส่งรายละเอียด
                  <Icon name="diagonal" size={20} />
                </button>
                {draftLink && (
                  <div className="form-feedback" role="status">
                    เตรียมข้อความแล้ว หากแอปอีเมลยังไม่เปิด{" "}
                    <a href={draftLink}>กดเปิดอีกครั้ง</a> หรือติดต่อ{" "}
                    <a href={company.phoneHref}>{company.phone}</a>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-main">
          <a
            className="footer-brand"
            href="#home"
            onClick={() => navigateTo("home")}
            aria-label="MPC กลับหน้าแรก"
          >
            <img src={Logo} width="112" height="61" alt="MPC" />
            <span>
              MECHANIC PROFESSIONAL<small>PRECISION IN EVERY DETAIL.</small>
            </span>
          </a>
          <div className="footer-links">
            <a href="#about">เกี่ยวกับเรา</a>
            <a href="#service">บริการของเรา</a>
            <a href="#contact">ติดต่อเรา</a>
          </div>
          <a className="back-to-top" href="#home" aria-label="กลับด้านบน">
            <Icon name="down" size={20} />
          </a>
        </div>
        <div className="container footer-bottom">
          <span>
            © {new Date().getFullYear()} Mechanic Professional Co., Ltd. All
            rights reserved.
          </span>
          <span>INDUSTRIAL EXPERTISE. HUMAN COMMITMENT.</span>
        </div>
      </footer>

      <div className="mobile-contact-bar">
        <a href={company.phoneHref}>
          <Icon name="phone" size={18} />
          โทรปรึกษางาน
        </a>
        <a href="#contact" onClick={() => navigateTo("contact")}>
          สอบถามบริการ
          <Icon name="diagonal" size={18} />
        </a>
      </div>

      <dialog
        className="service-dialog"
        ref={dialogRef}
        aria-labelledby="service-dialog-title"
        onClose={() => setSelectedService(null)}
        onClick={(event) => {
          if (event.target === event.currentTarget) dialogRef.current.close();
        }}
      >
        {selectedService && (
          <div className="dialog-content">
            <button
              className="dialog-close"
              type="button"
              onClick={() => dialogRef.current.close()}
              aria-label="ปิดรายละเอียดบริการ"
              autoFocus
            >
              <Icon name="close" />
            </button>
            <span className="dialog-icon">
              <Icon name={selectedService.icon} size={38} />
            </span>
            <p className="eyebrow">
              {selectedService.number} — {selectedService.english}
            </p>
            <h2 id="service-dialog-title">{selectedService.title}</h2>
            <p className="dialog-description">{selectedService.detail}</p>
            <h3>ขอบเขตบริการ</h3>
            <ul className="dialog-scope">
              {selectedService.scope.map((item) => (
                <li key={item}>
                  <Icon name="check" size={18} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="dialog-preparation">
              <h3>ข้อมูลที่ช่วยให้เราประเมินงานได้</h3>
              <p>{selectedService.preparation}</p>
            </div>
            <button
              className="button"
              type="button"
              onClick={() => askAboutService(selectedService)}
            >
              สอบถามบริการนี้
              <Icon name="diagonal" size={20} />
            </button>
          </div>
        )}
      </dialog>
    </div>
  );
}
